/**
 * Media Upload Zone Component
 * Generic upload component for images and Lottie animations
 * Supports drag-and-drop, preview, and optional cropping for images
 */

import { useCallback } from 'react';
import { Upload, X, Crop, FileJson, Image as ImageIcon } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { Card } from '@/core/components/ui/card';
import { cn } from '@/core/lib/utils';
import { AssetReference } from '../../types/asset-types';
import { PendingMediaUpload, MediaType } from '../../../kiosk/types/kiosk-types';
import { ALLOWED_LOGO_EXTENSIONS, ALLOWED_LOTTIE_EXTENSIONS, formatFileSize } from '../../utils/file-helpers';

interface MediaUploadZoneProps {
  label: string;
  target: 'media4' | 'media5';
  currentMedia?: AssetReference;
  pendingMedia?: PendingMediaUpload;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  onCrop?: () => void;
  helpText?: string;
}

export function MediaUploadZone({
  label,
  target,
  currentMedia,
  pendingMedia,
  disabled = false,
  onFileSelect,
  onRemove,
  onCrop,
  helpText,
}: MediaUploadZoneProps) {
  const handleDrop = useCallback(
    (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();

      if (disabled) return;

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        onFileSelect(files[0]);
      }
    },
    [disabled, onFileSelect]
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        onFileSelect(files[0]);
      }
      // Reset input value to allow selecting the same file again
      e.target.value = '';
    },
    [onFileSelect]
  );

  const hasMedia = pendingMedia || currentMedia;
  const displayUrl = pendingMedia?.previewUrl || currentMedia?.s3Url;

  // Determine media type from pending upload OR from saved media's content type
  let mediaType: MediaType | undefined = pendingMedia?.mediaType;
  if (!mediaType && currentMedia?.contentType) {
    // Infer media type from content type
    if (currentMedia.contentType.startsWith('image/')) {
      mediaType = 'image';
    } else if (currentMedia.contentType === 'application/json') {
      mediaType = 'lottie';
    }
  }

  const isImage = mediaType === 'image';
  const isLottie = mediaType === 'lottie';

  // Combined allowed extensions
  const allowedExtensions = [...ALLOWED_LOGO_EXTENSIONS, ...ALLOWED_LOTTIE_EXTENSIONS].join(', ');

  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm font-medium">{label}</p>
        <div className="flex items-center gap-2">
          {pendingMedia && (
            <>
              <span
                className={cn(
                  'text-xs px-2 py-1 rounded flex items-center gap-1',
                  isImage && 'bg-blue-100 text-blue-800',
                  isLottie && 'bg-purple-100 text-purple-800'
                )}
              >
                {isImage && <ImageIcon className="h-3 w-3" />}
                {isLottie && <FileJson className="h-3 w-3" />}
                {isImage ? 'Image' : 'Animation'}
              </span>
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                Pending Upload
              </span>
            </>
          )}
        </div>
      </div>

      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className={cn(
          'relative border-2 border-dashed rounded-lg p-6 transition-colors',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary',
          hasMedia ? 'border-gray-200' : 'border-gray-300'
        )}
      >
        {/* File Input - disabled pointer events when media exists to prevent conflicts */}
        <input
          type="file"
          id={`media-upload-${target}`}
          accept={allowedExtensions}
          onChange={handleFileInputChange}
          disabled={disabled}
          className={cn(
            'absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed',
            hasMedia && 'pointer-events-none'
          )}
        />

        {hasMedia && displayUrl ? (
          /* Preview Mode */
          <div className="space-y-3">
            <div className="relative w-full h-32 bg-gray-100 rounded flex items-center justify-center overflow-hidden">
              {isImage && (
                <img
                  src={displayUrl}
                  alt={`${target} media preview`}
                  className="max-w-full max-h-full object-contain"
                />
              )}
              {isLottie && (
                <div className="flex flex-col items-center justify-center gap-2 text-gray-600">
                  <FileJson className="h-12 w-12 text-purple-500" />
                  <p className="text-xs font-medium">Lottie Animation</p>
                </div>
              )}
            </div>

            {pendingMedia && (
              <div className="text-xs text-gray-600">
                <p>File: {pendingMedia.file.name}</p>
                <p>Size: {formatFileSize(pendingMedia.file.size)}</p>
                <p>Type: {isImage ? 'Image' : 'Lottie Animation'}</p>
              </div>
            )}

            <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
              {onCrop && isImage && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    onCrop();
                  }}
                  disabled={disabled}
                  className="flex-1"
                >
                  <Crop className="h-4 w-4 mr-2" />
                  Crop
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onRemove();
                }}
                disabled={disabled}
                className={cn(onCrop && isImage ? 'flex-1' : 'w-full')}
              >
                <X className="h-4 w-4 mr-2" />
                Remove
              </Button>
            </div>
          </div>
        ) : (
          /* Upload Mode */
          <div className="text-center space-y-2">
            <Upload className="h-8 w-8 mx-auto text-gray-400" />
            <div className="text-sm">
              <p className="font-medium text-gray-700">Drop media here or click to upload</p>
              <p className="text-gray-500 text-xs mt-1">
                Images: {ALLOWED_LOGO_EXTENSIONS.join(', ').toUpperCase()} (max 2MB)
              </p>
              <p className="text-gray-500 text-xs">
                Lottie: {ALLOWED_LOTTIE_EXTENSIONS.join(', ').toUpperCase()} (max 1MB)
              </p>
            </div>
          </div>
        )}
      </div>

      {!hasMedia && helpText && (
        <p className="text-xs text-gray-500">{helpText}</p>
      )}
    </Card>
  );
}
